import type { Outcome } from "@landing/types"
import { useCallback, useEffect, useRef } from "react"
import {
  applyDominantConfigs,
  calculateVolatility,
  clampAndNormalize,
  type DominantConfig,
  formatPrice,
  generateNewsEvent,
  generatePriceChange,
  normalizeOutcomes,
  PRICE_CONSTRAINTS,
  parsePrice,
  sortOutcomes,
  type TradeUpdate,
  updateMomentum,
} from "./utils/tradingSimulation"

interface UseSimulatedTradingParams {
  enabled: boolean
  heldOutcomesLength: number
  title: string
  setOutcomes: React.Dispatch<React.SetStateAction<Outcome[]>>
  setRecentTrades: React.Dispatch<React.SetStateAction<Record<string, TradeUpdate>>>
  dominantConfigs?: DominantConfig[]
}

export function useSimulatedTrading({
  enabled,
  setOutcomes,
  setRecentTrades,
  dominantConfigs,
}: UseSimulatedTradingParams) {
  const momentumRef = useRef(new Map<string, number>())
  const tickRef = useRef(0)

  // Process a single trading tick
  const processTradingTick = useCallback(() => {
    setOutcomes((currentOutcomes) => {
      let newOutcomes = [...currentOutcomes]
      const momentum = momentumRef.current

      // Pick random outcomes to update
      const numToUpdate = Math.floor(Math.random() * 2) + 1
      const indicesToUpdate = new Set<number>()
      while (indicesToUpdate.size < numToUpdate) {
        indicesToUpdate.add(Math.floor(Math.random() * newOutcomes.length))
      }

      let totalChange = 0
      const newsEvent = generateNewsEvent(newOutcomes.length)

      // Update selected outcomes
      for (const index of indicesToUpdate) {
        const outcome = newOutcomes[index]
        if (!outcome) continue

        const currentMomentum = momentum.get(outcome.name) || 0
        const volatility = calculateVolatility(outcome.seed ? Number.parseInt(outcome.seed, 10) : undefined)
        const change = generatePriceChange(currentMomentum, volatility, newsEvent, index)
        const currentPrice = parsePrice(outcome.price)

        if (currentPrice + change < PRICE_CONSTRAINTS.MIN_PRICE) continue

        newOutcomes[index] = {
          ...outcome,
          price: formatPrice(currentPrice + change),
        }
        totalChange += change

        // Update momentum
        momentum.set(outcome.name, updateMomentum(currentMomentum, change))

        // Record trade
        if (change !== 0 && outcome.name) {
          setRecentTrades((prev) => ({
            ...prev,
            [outcome.name]: {
              type: change > 0 ? "buy" : "sell",
              timestamp: Date.now(),
            },
          }))
        }
      }

      // Balance remaining outcomes
      const remainingIndices = Array.from({ length: newOutcomes.length })
        .map((_, i) => i)
        .filter((i) => !indicesToUpdate.has(i))

      let remaining = -totalChange
      while (Math.abs(remaining) > 0.01 && remainingIndices.length > 0) {
        const randomIdx = Math.floor(Math.random() * remainingIndices.length)
        const idx = remainingIndices[randomIdx]
        if (idx === undefined) break

        const outcome = newOutcomes[idx]
        if (!outcome) {
          remainingIndices.splice(randomIdx, 1)
          continue
        }

        const currentMomentum = momentum.get(outcome.name) || 0
        const direction = remaining > 0 ? 0.125 : -0.125
        const adjustedDirection = direction + currentMomentum * 0.15
        const currentPrice = parsePrice(outcome.price)

        if (currentPrice + adjustedDirection < PRICE_CONSTRAINTS.MIN_PRICE) {
          remainingIndices.splice(randomIdx, 1)
          continue
        }

        newOutcomes[idx] = {
          ...outcome,
          price: formatPrice(currentPrice + adjustedDirection),
        }
        remaining -= adjustedDirection
        momentum.set(outcome.name, currentMomentum * 0.85 + adjustedDirection * 0.15)

        if (Math.random() > 0.7) {
          remainingIndices.splice(randomIdx, 1)
        }
      }

      // Periodic normalization
      tickRef.current++
      if (tickRef.current % 5 === 0) {
        newOutcomes = normalizeOutcomes(newOutcomes)
      }

      // Apply dominant configurations if provided
      if (dominantConfigs && dominantConfigs.length > 0) {
        newOutcomes = applyDominantConfigs(newOutcomes, dominantConfigs)
      }

      // Clamp prices and normalize
      newOutcomes = clampAndNormalize(newOutcomes)

      // Final sorting
      return sortOutcomes(newOutcomes)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dominantConfigs]) // setOutcomes and setRecentTrades are stable and don't need to be dependencies

  // Clean up old trades
  const cleanupTrades = useCallback(() => {
    setRecentTrades((prev) => {
      const now = Date.now()
      const updated = { ...prev }
      let hasChanges = false

      for (const [key, value] of Object.entries(updated)) {
        if (now - value.timestamp > 250) {
          delete updated[key]
          hasChanges = true
        }
      }

      // Only return new object if there were actual changes
      return hasChanges ? updated : prev
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // setRecentTrades is stable and doesn't need to be a dependency

  // Main effect to run the simulation
  useEffect(() => {
    if (!enabled) return

    // Reset state
    momentumRef.current = new Map()
    tickRef.current = 0

    // Start trading simulation
    const tradingInterval = setInterval(processTradingTick, 2000)

    // Start trade cleanup (reduced frequency from 50ms to 100ms)
    const cleanupInterval = setInterval(cleanupTrades, 100)

    return () => {
      clearInterval(tradingInterval)
      clearInterval(cleanupInterval)
    }
  }, [enabled, processTradingTick, cleanupTrades])
}
